/**
 * Rhythmz — Scene: Geometric Mandala
 * ──────────────────────────────────
 * Sacred-geometry kaleidoscope of rotating rings, stars, and patterns.
 * All elements rotate at golden-ratio-based speeds. Converges on drop.
 *
 * Audio: Bass → rotation speed + size. Beat → center flash + ring ripple.
 */

import * as THREE from 'three/webgpu';
import { Fn, vec4, float, fract, sin, mul, add, positionLocal } from 'three/tsl';
import { SceneLighting, type AudioRef } from './SceneLighting';
import type { SceneHandle } from './SceneManager';

type N = any;

export interface MandalaUniforms {
  uTime: N; uBass: N; uMid: N; uBeat: N; uTreble: N;
  uCol0: N; uCol1: N; uCol2: N;
}

const PHI = 1.61803398875;

export function createGeometricMandala(u: MandalaUniforms): SceneHandle {
  const group = new THREE.Group();
  const lighting = new SceneLighting({
    keyColor: new THREE.Color(0.9, 0.3, 0.9),
    keyIntensity: 8, keyPosition: new THREE.Vector3(0, 0, 5),
    fillColor: new THREE.Color(0.3, 0.9, 0.9),
    fillIntensity: 6, fillPosition: new THREE.Vector3(5, 0, 0),
    rimColor: new THREE.Color(0.9, 0.8, 0.2),
    rimIntensity: 5, rimPosition: new THREE.Vector3(-5, 0, 0),
    camLightIntensity: 2,
  });
  group.add(lighting.group);

  const goldMat = new THREE.MeshStandardNodeMaterial({ metalness: 0.9, roughness: 0.15 });
  goldMat.emissiveNode = Fn(() => {
    const colorT = fract(add(mul(u.uTime, float(0.1)), mul(positionLocal.x, float(0.2))));
    const r = add(float(0.3), mul(sin(mul(colorT, float(6.28))), float(0.3)));
    const g = add(float(0.2), mul(sin(add(mul(colorT, float(6.28)), float(2.0))), float(0.3)));
    const b = add(float(0.5), mul(sin(add(mul(colorT, float(6.28)), float(4.0))), float(0.3)));
    const beatGlow = mul(u.uBeat, float(1.0));
    return vec4(add(r, beatGlow), add(g, mul(beatGlow, float(0.5))), b, float(1.0));
  })();

  // ── Outer torus ring ──
  const outerGeo = new THREE.TorusGeometry(1.5, 0.04, 8, 64);
  const outer = new THREE.Mesh(outerGeo, goldMat);
  group.add(outer);

  // ── Inner rings (instanced, different rotations) ──
  const ringCount = 6;
  const ringGeo = new THREE.TorusGeometry(1.0, 0.03, 6, 48);
  const rings = new THREE.InstancedMesh(ringGeo, goldMat, ringCount);
  rings.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  group.add(rings);

  // ── Star pattern: instanced cones radiating ──
  const starCount = 12;
  const starGeo = new THREE.ConeGeometry(0.04, 0.5, 4);
  const stars = new THREE.InstancedMesh(starGeo, goldMat, starCount);
  stars.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  group.add(stars);

  // ── Center dodecahedron ──
  const centerGeo = new THREE.DodecahedronGeometry(0.3, 0);
  const centerMat = new THREE.MeshStandardNodeMaterial({ metalness: 0.5, roughness: 0.2 });
  centerMat.emissiveNode = Fn(() => {
    const flash = add(float(0.5), mul(u.uBeat, float(3.0)));
    return vec4(mul(float(0.9), flash), mul(float(0.7), flash), mul(float(0.1), flash), float(1.0));
  })();
  const center = new THREE.Mesh(centerGeo, centerMat);
  group.add(center);

  let t = 0;
  const pivot = new THREE.Object3D();

  const update = (dt: number): void => {
    t += dt;
    const audio: AudioRef = {
      bass: u.uBass.value ?? 0, beat: u.uBeat.value ?? 0, mid: u.uMid.value ?? 0,
      level: ((u.uBass.value ?? 0) + (u.uMid.value ?? 0)) * 0.5,
    };
    lighting.update(audio);

    const speed = 0.5 + (u.uBass.value ?? 0) * 1.5;
    const converge = (u.uBeat.value ?? 0) * 0.3;

    // Outer ring
    outer.rotation.z = t * speed * 0.3;
    outer.scale.setScalar(1 + converge * 0.1);

    // Inner rings — golden ratio rotation speeds
    for (let i = 0; i < ringCount; i++) {
      const ratio = Math.pow(PHI, i - ringCount / 2);
      pivot.position.set(0, 0, 0);
      pivot.rotation.set(t * speed * ratio * 0.5, t * speed * ratio * 0.3, t * speed * 0.2 * (i % 2 ? 1 : -1));
      const scale = (0.5 + i * 0.15) * (1 + converge * 0.05);
      pivot.scale.setScalar(scale);
      pivot.updateMatrix();
      rings.setMatrixAt(i, pivot.matrix);
    }
    rings.instanceMatrix.needsUpdate = true;

    // Stars radiate outward
    for (let i = 0; i < starCount; i++) {
      const angle = (i / starCount) * Math.PI * 2 + t * speed * 0.2;
      const r = 1.2 + converge * 0.5;
      pivot.position.set(Math.cos(angle) * r, Math.sin(angle) * r, 0);
      pivot.rotation.set(0, 0, angle + Math.PI / 2);
      pivot.scale.set(1, 1 + (u.uTreble.value ?? 0) * 0.5, 1);
      pivot.updateMatrix();
      stars.setMatrixAt(i, pivot.matrix);
    }
    stars.instanceMatrix.needsUpdate = true;

    // Center flash + spin
    center.rotation.set(t * speed, t * speed * 1.3, t * speed * 0.7);
    center.scale.setScalar(1 + (u.uBeat.value ?? 0) * 0.5);

    group.rotation.y = t * 0.05;
  };

  const dispose = (): void => {
    lighting.dispose();
    outerGeo.dispose(); goldMat.dispose();
    ringGeo.dispose();
    starGeo.dispose();
    centerGeo.dispose(); centerMat.dispose();
    group.clear();
  };

  return { group, update, dispose, name: 'Geometric Mandala' };
}